/**
 * FAQ Listing module
 *
 * Static or animated DNA sequence strand
 */

// Deps
import { FelaComponent, createComponent } from "react-fela";
import { useState, useEffect, useContext } from "react";
import { combineRules } from "fela";
import { gutters, colors } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import { textStyles } from "~/styleguide/typography";
import { DataContext } from "~/context/content";
import get from "lodash.get";

// Components
import Item from "./item";
import { ContentMaxWidth } from "~/components/layout/max-width";

export default function FAQListing({ block }) {
    const { heading, entries, anchor_id } = block;
    const pageData = useContext(DataContext);
    const permaLink = get(pageData, "link", pageData);
    const [faqSchema, setFaqSchema] = useState(null);

    useEffect(() => {
        // Create the simplified FAQ page schema once for all items
        const mainEntity = entries.map(entry => ({
            "@type": "Question",
            "name": entry.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": entry.answer
            }
        }));

        const pageSchema = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": mainEntity,
            "mainEntityOfPage": permaLink
        };

        setFaqSchema(pageSchema);
    }, [entries]);

    return (
        <Container id={anchor_id} className="module-faq">
            <ContentMaxWidth>
                {/* Inject the FAQ schema with mainEntityOfPage pointing to the parent article */}
                {faqSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(faqSchema)}
                </script>
                )}
                {/* Heading */}
                {heading && (
                    <FelaComponent
                        as="h3"
                        style={combineRules(textStyles.heading3, faqHeading)}
                    >
                        <span dangerouslySetInnerHTML={{ __html: heading }} />
                    </FelaComponent>
                )}

                <Listing>
                    {entries &&
                        entries.map((entry, index) => {
                            return <Item key={index} item={entry} index={index} />;
                        })}
                </Listing>
            </ContentMaxWidth>
        </Container>
    );
}

export const faqHeading = {
    extend: [fluid("marginBottom", { min: 40, max: 80 })]
};

export const Container = createComponent(
    () => ({
        textAlign: "center",
        marginTop: "30px",
        backgroundColor: colors.lightGrey,
        extend: [
            fluid("paddingH", gutters.default),
            fluid("paddingV", gutters.vertical)
        ]
    }),
    "div",
    ["className", "id"]
);

const Listing = createComponent(() => ({
    textAlign: "left"
}));
